import { extractFuturesRoot } from '../../constants/contractMultipliers';
import { isIndexOrCfdInstrument } from '../../constants/indexInstruments';
import { normalizeBars as normalizeChartBars } from '../chart/ChartDataAdapter';
import {
  resolveTradeChartSource,
  toRequestTimeframe,
  type TradeChartFetchAttempt,
  type TradeChartTimeframe,
} from '../chart/tradeChartResolution';
import type { MetricBar, MetricExecution } from './deterministicTradeMetrics';
import {
  normalizeReplayExecutions,
  type ReplayBarInput,
  type ReplayExecutionInput,
} from './replayEngine';
import { buildTradeDetailsDeterministicSummary } from './tradeDetailsDeterministicAdapter';

export type TradeExcursionKind = 'MFE' | 'MAE';

export type TradeExcursionSummary = {
  avgPositionMfe: number;
  avgPositionMae: number;
  phaseCount: number;
};

const POSITION_MFE_FIELDS = [
  'positionMFE',
  'positionMfe',
  'avgPositionMfe',
  'avgPositionMFE',
  'deterministicMfe',
  'deterministicMFE',
] as const;

const POSITION_MAE_FIELDS = [
  'positionMAE',
  'positionMae',
  'avgPositionMae',
  'avgPositionMAE',
  'deterministicMae',
  'deterministicMAE',
] as const;

function toFiniteNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getTradeExecutionRows(trade: any): ReplayExecutionInput[] {
  if (Array.isArray(trade?.executionsList)) return trade.executionsList as ReplayExecutionInput[];
  if (Array.isArray(trade?.executions)) return trade.executions as ReplayExecutionInput[];
  return [];
}

function getTradeAndExecutionRows(trade: any): any[] {
  return [trade, ...getTradeExecutionRows(trade)];
}

function getBaseCandidates(): string[] {
  let envBase = '';
  try {
    envBase = (import.meta as any)?.env?.VITE_API_BASE || '';
  } catch {
    envBase = '';
  }

  const cleanEnv = String(envBase).replace(/\/+$/, '');
  const bases = cleanEnv ? [cleanEnv, ''] : [''];
  return Array.from(new Set(bases));
}

function extractRawBars(payload: any): any[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.bars)) return payload.bars;
  if (Array.isArray(payload?.candles)) return payload.candles;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.bars?.bars)) return payload.bars.bars;
  if (Array.isArray(payload?.bars?.data)) return payload.bars.data;
  return [];
}

function normalizeAssetClass(value: unknown): 'STK' | 'FUT' | 'CASH' | 'CRYPTO' | null {
  const raw = String(value ?? '').trim().toUpperCase();
  if (!raw) return null;
  if (raw.includes('FUT')) return 'FUT';
  if (raw === 'FX' || raw.includes('FOREX') || raw.includes('CASH') || raw.includes('CURRENCY')) return 'CASH';
  if (raw.includes('CRYPTO')) return 'CRYPTO';
  if (raw.includes('STK') || raw.includes('STOCK') || raw.includes('EQUITY')) return 'STK';
  return null;
}

function inferTradeChartAssetClass(trade: any): 'STK' | 'FUT' | 'CASH' | 'CRYPTO' {
  const execution = getTradeExecutionRows(trade)[0] as any;
  const explicit =
    normalizeAssetClass(trade?.market) ??
    normalizeAssetClass(trade?.marketType) ??
    normalizeAssetClass(trade?.assetClass) ??
    normalizeAssetClass(trade?.category) ??
    normalizeAssetClass(trade?.instrumentType) ??
    normalizeAssetClass(trade?.securityType) ??
    normalizeAssetClass(trade?.instrument?.secType) ??
    normalizeAssetClass(execution?.market) ??
    normalizeAssetClass(execution?.assetClass) ??
    normalizeAssetClass(execution?.instrument?.secType);

  if (explicit) return explicit;

  const symbol = String(trade?.symbol ?? execution?.symbol ?? trade?.localSymbol ?? '').trim().toUpperCase();
  if (extractFuturesRoot(symbol)) return 'FUT';
  if (/^[A-Z]{3}\/[A-Z]{3}$/.test(symbol)) return 'CASH';
  if (/^(BTC|ETH|SOL|XRP|DOGE|ADA|BNB)\b/.test(symbol)) return 'CRYPTO';
  return 'STK';
}

function getTradeSymbol(trade: any): string {
  const execution = getTradeExecutionRows(trade)[0] as any;
  return String(trade?.symbol ?? execution?.symbol ?? trade?.localSymbol ?? '').trim().toUpperCase();
}

function getTradeExactContract(trade: any): { localSymbol?: string | null; lastTradeDateOrContractMonth?: string | null; conid?: string | number | null } {
  const execution = getTradeExecutionRows(trade)[0] as any;
  return {
    localSymbol: trade?.localSymbol ?? execution?.localSymbol ?? null,
    lastTradeDateOrContractMonth:
      trade?.lastTradeDateOrContractMonth ??
      trade?.expiry ??
      trade?.contractMonth ??
      execution?.lastTradeDateOrContractMonth ??
      execution?.expiry ??
      execution?.contractMonth ??
      null,
    conid: trade?.conid ?? trade?.conId ?? execution?.conid ?? execution?.conId ?? null,
  };
}

function getTradeExchange(trade: any): string | null {
  const execution = getTradeExecutionRows(trade)[0] as any;
  const value = trade?.exchange ?? trade?.primaryExchange ?? execution?.exchange ?? execution?.primaryExchange ?? null;
  const text = String(value ?? '').trim();
  return text || null;
}

function getTradeCurrency(trade: any): string | null {
  const execution = getTradeExecutionRows(trade)[0] as any;
  const value = trade?.currency ?? execution?.currency ?? trade?.instrument?.currency ?? execution?.instrument?.currency ?? null;
  const text = String(value ?? '').trim();
  return text || null;
}

function toMetricBars(bars: ReplayBarInput[]): MetricBar[] {
  return (bars || [])
    .map((bar) => ({
      time: Number((bar as any)?.time),
      high: Number((bar as any)?.high),
      low: Number((bar as any)?.low),
    }))
    .filter((bar) => Number.isFinite(bar.time) && Number.isFinite(bar.high) && Number.isFinite(bar.low))
    .sort((a, b) => a.time - b.time);
}

function toMetricExecutions(executions: ReplayExecutionInput[], bars: ReplayBarInput[], multiplier: number): MetricExecution[] {
  return normalizeReplayExecutions(executions, bars, { multiplier })
    .map((execution): MetricExecution => ({
      id: execution.sourceId,
      timestamp: Math.floor(execution.timestampMs / 1000),
      side: execution.side,
      qty: execution.qty,
      price: execution.price,
      fee: execution.executionCostsPaid,
    }))
    .filter((execution) => Number.isFinite(execution.timestamp) && Number.isFinite(execution.qty) && execution.qty > 0 && Number.isFinite(execution.price));
}

async function fetchAttemptPayload(input: {
  attempt: TradeChartFetchAttempt;
  exchange?: string | null;
  currency?: string | null;
  signal: AbortSignal;
}, dateYmd?: string): Promise<any> {
  const query = new URLSearchParams();
  query.set('symbol', input.attempt.symbol);
  query.set('secType', input.attempt.assetClass);
  query.set('timeframe', toRequestTimeframe(input.attempt.timeframe));
  query.set('useRTH', '0');

  if (dateYmd) query.set('date', dateYmd);
  // Tag excursion-origin requests for backend instrumentation
  query.set('caller', 'tradeExcursions');

  if (input.exchange) query.set('exchange', input.exchange);
  if (input.currency) query.set('currency', input.currency);
  if (input.attempt.qualifiers?.localSymbol) query.set('localSymbol', String(input.attempt.qualifiers.localSymbol));
  if (input.attempt.qualifiers?.lastTradeDateOrContractMonth) query.set('lastTradeDateOrContractMonth', String(input.attempt.qualifiers.lastTradeDateOrContractMonth));
  if (input.attempt.qualifiers?.conid) query.set('conid', String(input.attempt.qualifiers.conid));

  let lastPayload: any = null;
  for (const base of getBaseCandidates()) {
    const url = `${base}/api/market/ohlc?${query.toString()}`;
    try {
      const response = await fetch(url, { signal: input.signal });
      let payload: any = null;
      try {
        payload = await response.json();
      } catch {
        payload = { error: true, message: 'Invalid JSON response' };
      }
      payload.status = response.status;
      if (!response.ok) {
        payload.error = true;
      }
      lastPayload = payload;
      if (response.ok) return payload;
    } catch (error: any) {
      if (error?.name === 'AbortError') throw error;
      lastPayload = { error: true, message: String(error?.message ?? error) };
    }
  }

  return lastPayload;
}

// Compute a canonical YMD date (America/New_York) for a trade using its executions or timestamps.
function computeExecutionDateYmdForTrade(trade: any): string {
  const executions = getTradeExecutionRows(trade);
  let maxSec: number | null = null;

  for (const e of executions) {
    const candidates = [
      e?.timestamp,
      e?.openTimestampMs,
      e?.time,
      e?.executedAt,
      e?.date,
      e?.t,
    ];
    for (const c of candidates) {
      const n = Number(c);
      if (Number.isFinite(n)) {
        const sec = n > 1e12 ? Math.floor(n / 1000) : Math.floor(n);
        if (sec > 0 && (maxSec == null || sec > maxSec)) maxSec = sec;
      }
    }
  }

  const tradeCandidates = [
    trade?.closeTimestampMs,
    trade?.exitTimestampMs,
    trade?.closeTime,
    trade?.exitTime,
    trade?.closeDate,
    trade?.exitDate,
    trade?.openTimestampMs,
    trade?.entryTimestampMs,
    trade?.openTime,
    trade?.entryTime,
    trade?.timestamp,
    trade?.date,
  ];
  for (const c of tradeCandidates) {
    const n = Number(c);
    if (Number.isFinite(n)) {
      const sec = n > 1e12 ? Math.floor(n / 1000) : Math.floor(n);
      if (sec > 0 && (maxSec == null || sec > maxSec)) maxSec = sec;
    }
  }

  if (maxSec == null) maxSec = Math.floor(Date.now() / 1000);

  const dt = new Date(maxSec * 1000);
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(dt);
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${map.year}-${map.month}-${map.day}`;
}

export function readPersistedTradePositionExcursionValue(trade: any, kind: TradeExcursionKind): number | null {
  const fields = kind === 'MFE' ? POSITION_MFE_FIELDS : POSITION_MAE_FIELDS;
  for (const row of getTradeAndExecutionRows(trade)) {
    for (const field of fields) {
      const value = toFiniteNumber((row as any)?.[field]);
      if (value != null) return value;
    }
  }
  return null;
}

export function buildCanonicalTradeExcursionSummary(input: {
  executions: ReplayExecutionInput[];
  bars: ReplayBarInput[];
  multiplier?: number;
}): TradeExcursionSummary | null {
  const multiplier = Number.isFinite(Number(input.multiplier)) ? Number(input.multiplier) : 1;
  const metricBars = toMetricBars(input.bars || []);
  if (metricBars.length === 0) return null;

  const metricExecutions = toMetricExecutions(input.executions || [], input.bars || [], multiplier);
  if (metricExecutions.length === 0) return null;

  const summary = buildTradeDetailsDeterministicSummary({
    executions: metricExecutions,
    bars: metricBars,
    multiplier,
    clampEfficiency: false,
    debug: false,
  });

  return {
    avgPositionMfe: summary.avgPositionMfe,
    avgPositionMae: summary.avgPositionMae,
    phaseCount: summary.phaseCount,
  };
}

export async function fetchCanonicalTradeExcursionSummary(input: {
  trade: any;
  signal: AbortSignal;
  multiplier?: number;
  requestedTimeframe?: TradeChartTimeframe;
}): Promise<TradeExcursionSummary | null> {
  const executions = getTradeExecutionRows(input.trade);
  if (executions.length === 0) return null;

  const symbol = getTradeSymbol(input.trade);
  if (!symbol) return null;
  if (isIndexOrCfdInstrument(symbol, input.trade?.instrumentType ?? input.trade?.instrument, input.trade)) {
    return null;
  }

  const assetClass = inferTradeChartAssetClass(input.trade);
  const exactContract = getTradeExactContract(input.trade);
  const exchange = getTradeExchange(input.trade);
  const currency = getTradeCurrency(input.trade);

  const loadResult = await resolveTradeChartSource({
    assetClass,
    requestedSymbol: symbol,
    requestedTimeframe: input.requestedTimeframe ?? '1m',
    exactContract,
    fetchBars: async (attempt) => {
      const dateYmd = computeExecutionDateYmdForTrade(input.trade);
      const payload = await fetchAttemptPayload({ attempt, exchange, currency, signal: input.signal }, dateYmd);
      let candles: ReplayBarInput[] = [];
      try {
        candles = normalizeChartBars(extractRawBars(payload)) as ReplayBarInput[];
      } catch {
        candles = [];
      }
      return { payload, candles };
    },
  });

  if (!loadResult.resolution.metricsAllowed || !Array.isArray(loadResult.candles) || loadResult.candles.length === 0) {
    return null;
  }

  return buildCanonicalTradeExcursionSummary({
    executions,
    bars: loadResult.candles as ReplayBarInput[],
    multiplier: input.multiplier,
  });
}